import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { clearConsent } from "@/lib/cookies";
import SEO from "@/components/SEO";

const cookies = [
  {
    name: "cookie-consent",
    type: "Essential",
    provider: "KEISH (localStorage)",
    purpose: "Stores your cookie preference so the consent banner doesn't reappear on every visit.",
    duration: "Persistent (until cleared)",
  },
  {
    name: "_ga",
    type: "Analytics",
    provider: "Google LLC",
    purpose: "Distinguishes unique visitors by assigning a randomly generated number as a client identifier.",
    duration: "2 years",
  },
  {
    name: "_ga_<container-id>",
    type: "Analytics",
    provider: "Google LLC",
    purpose: "Used by Google Analytics 4 to maintain session state across page loads.",
    duration: "2 years",
  },
];

const Cookies = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Cookie Policy — KEISH Design Studio"
        description="Learn about the cookies used on keish.studio, how they work, and how to manage your preferences."
      />
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors mb-12"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </Link>

          <h1 className="font-display italic text-4xl md:text-5xl tracking-[-0.02em] mb-4">
            Cookie Policy
          </h1>
          <p className="font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground mb-12">
            Last Updated: February 14, 2026
          </p>

          <div className="space-y-10 font-body text-ts-xs md:text-ts-sm leading-relaxed text-foreground/80">
            {/* Overview */}
            <section>
              <p>
                This page explains what cookies and similar storage mechanisms we use on{" "}
                <strong className="text-foreground">keish.studio</strong>, why we use them, and how you
                can control them. For a broader look at how we handle your data, see our{" "}
                <Link to="/privacy" className="underline hover:text-foreground transition-colors">
                  Privacy Policy
                </Link>.
              </p>
            </section>

            {/* What Are Cookies */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                What Are Cookies?
              </h2>
              <p>
                Cookies are small text files stored on your device by your browser. We also use
                localStorage, a similar browser-based storage mechanism. These technologies help us
                remember your preferences and understand how you use our site.
              </p>
            </section>

            {/* Cookie Table */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                Cookies We Use
              </h2>
              <p className="mb-4">
                We only load analytics cookies <strong className="text-foreground">after</strong> you
                click "Accept" on our consent banner. If you decline, only the essential preference
                storage is used.
              </p>

              <div className="border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-left text-[13px] min-w-[640px]">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                        Provider
                      </th>
                      <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                        Purpose
                      </th>
                      <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {cookies.map((c) => (
                      <tr key={c.name}>
                        <td className="px-4 py-3 font-body text-ts-2xs text-foreground">{c.name}</td>
                        <td className="px-4 py-3">{c.type}</td>
                        <td className="px-4 py-3">{c.provider}</td>
                        <td className="px-4 py-3">{c.purpose}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{c.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Managing Cookies */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                Managing Your Preferences
              </h2>
              <p className="mb-3">You can change your cookie choice at any time:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  Click{" "}
                  <button
                    onClick={() => {
                      clearConsent();
                      window.dispatchEvent(new Event("show-cookie-banner"));
                    }}
                    className="underline hover:text-foreground transition-colors"
                  >
                    Cookie Settings
                  </button>{" "}
                  in our site footer (or right here) to re-open the consent banner.
                </li>
                <li>
                  Adjust your browser's cookie settings to block or delete cookies.
                </li>
                <li>
                  Install the{" "}
                  <a
                    href="https://tools.google.com/dlpage/gaoptout"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground transition-colors"
                  >
                    Google Analytics Opt-out Add-on
                  </a>{" "}
                  for an additional layer of control.
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                Questions?
              </h2>
              <p>
                If you have questions about our use of cookies, reach out at{" "}
                <a
                  href="mailto:madi@keish.studio"
                  className="underline hover:text-foreground transition-colors"
                >
                  madi@keish.studio
                </a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cookies;
